import React, { useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TRACKABLE_EVENTS } from 'helpers/trackerHelpers';
import { BudgetProposalT } from 'routes/BudgetModule/config/types';
import AuxiliaryTabs from 'components/SharedComponents/AuxiliaryTabs/AuxiliaryTabs';
import { styled } from '@mui/material/styles';
import { Box } from '@mui/material';
import { useGetScopes } from 'routes/BudgetModule/shared/useGetScopes';
import { useGetUserScopes } from 'routes/BudgetModule/shared/useGetUserScopes';
import {
    ALL_OPERATION_FILE_PREFIX,
    getExcelFilename,
} from 'routes/BudgetModule/contexts/helpers';
import { BudgetExpenseOrIncome } from 'business/constants';
import TabActions from '../TabActions';
import DraftOperations from './DraftOperations';
import FinancesSoftwareOperations from './FinancesSoftwareOperations';
import { getExpenseIncomeValueFromString } from '../AllInscriptions/helpers/allInscriptionsFetchUtils';
import { useGetInscriptionsWithOperationData } from 'routes/BudgetModule/helpers/hooks/useGetInscriptions';
import FullScreenProvider from 'contexts/FullScreenContextProvider';
import { useSelectSecondaryBudgetAxisSourceTable } from 'routes/BudgetModule/components/BudgetWithProvider/SecondaryBudgetAxisSourceTableContext';

type Props = {
    filteredBudgetProposals: BudgetProposalT[];
    allBudgetProposals: BudgetProposalT[];
    year: string;
    budgetName: string;
};

const StyTabulatedChipButtonGroupBudget = styled(AuxiliaryTabs, {
    name: 'StyTabulatedChipButtonGroupBudget',
})({
    margin: '0 3rem 2rem 3rem',
    '&&& .ag-row-group': {
        display: 'flex',
        alignItems: 'center',
    },
    '&&& .ag-cell': {
        display: 'flex',
        alignItems: 'center',
        border: 'none',
        lineHeight: '16px',
        fontSize: '11px',
    },
});

export default function AllOperations(props: Props): React.ReactElement {
    const { t } = useTranslation();
    const { year, filteredBudgetProposals, allBudgetProposals, budgetName } =
        props;
    const { tab, expenseOrIncome = BudgetExpenseOrIncome.EXPENSE } = useParams<{
        tab: string;
        expenseOrIncome: BudgetExpenseOrIncome;
    }>();

    const [hideEmptyOperations, setHideEmptyOperations] =
        useState<boolean>(true);

    const budgetBy = _.head(filteredBudgetProposals)?.by;

    const userScopes = useGetUserScopes();
    const scopes = useGetScopes();

    const referenceYear: number = parseInt(year, 10);

    const expenseOrIncomeValue =
        getExpenseIncomeValueFromString(expenseOrIncome);

    const {
        data: rawInscriptionsData,
        isLoading: areInscriptionsLoading,
        cacheKey: queryCacheKey,
    } = useGetInscriptionsWithOperationData({
        exercice: referenceYear,
        budgetBy: filteredBudgetProposals[0].by,
        expenseOrIncome: expenseOrIncomeValue,
    });

    const sourceTable = useSelectSecondaryBudgetAxisSourceTable();
    const inscriptionsData = rawInscriptionsData
        ? rawInscriptionsData.data
        : [];
    const tabsList = [
        {
            label: t('budget.expense'),
            pathname: `/budget_overview/${tab}/expense`,
        },
        {
            label: t('budget.income'),
            pathname: `/budget_overview/${tab}/income`,
        },
    ].map((tabItem) => ({
        ...tabItem,
        key: tabItem.label,
        tabActions: (
            <TabActions
                queryToRefresh={queryCacheKey}
                hideEmpty={hideEmptyOperations}
                setHideEmpty={setHideEmptyOperations}
                trackedEvents={
                    TRACKABLE_EVENTS.budget.budgetOverview.operations
                }
                toggleLabel={t(
                    'budgetProposal.hideEmptySecondaryBudgetAxisElements',
                    { context: sourceTable },
                )}
                fileName={getExcelFilename(
                    `${ALL_OPERATION_FILE_PREFIX}_${sourceTable}`,
                    [expenseOrIncome],
                )}
            />
        ),
        children: (
            <Box sx={{ py: 2 }}>
                <FullScreenProvider>
                    <FinancesSoftwareOperations
                        allBudgetProposals={allBudgetProposals}
                        referenceYear={referenceYear}
                        budgetBy={budgetBy!}
                        expenseOrIncome={expenseOrIncome}
                        userScopes={userScopes}
                        scopes={scopes}
                        hideEmptyOperations={hideEmptyOperations}
                        budgetName={budgetName}
                        inscriptionsData={inscriptionsData}
                        areInscriptionsLoading={areInscriptionsLoading}
                    />
                </FullScreenProvider>
                <FullScreenProvider>
                    <DraftOperations
                        referenceYear={referenceYear}
                        expenseOrIncome={expenseOrIncome}
                        budgetName={budgetName}
                        budgetBy={budgetBy!}
                    />
                </FullScreenProvider>
            </Box>
        ),
        disabled: false,
    }));

    return <StyTabulatedChipButtonGroupBudget tabsList={tabsList} />;
}
